// pages/CaseDetail.jsx - COMPLETE WITH FILE UPLOADS
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Building2,
  User,
  Calendar,
  Clock,
  FileText,
  Paperclip,
  MessageSquare,
  Activity,
  Phone,
  Mail,
  Globe,
  ChevronDown,
  ChevronRight,
  MapPin,
  ExternalLink,
  X,
  Plus,
  Save,
  Loader,
  Bell,
  BellOff,
  PhoneCall,
  MoreVertical,
  Image,
} from "lucide-react";
import apiService from "../../services/api";
import AccountDetailModal from "../AccountDetailModal";
import BookCaseAppointmentModal from "../../components/BookCaseAppointmentModal";
import SendCaseEmailModal from "../../components/SendCaseEmailModal";
import CaseEmailThread from "../../components/CaseEmailThread";
import CaseFeedItems from "../../components/dashboard/CaseFeedItems";
import PhoneLink from "../../components/PhoneLink";
import { useTheme } from "../../contexts/ThemeContext";

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [expandedSections, setExpandedSections] = useState({
    caseInfo: true,
    description: true,
    webInfo: true,
  });
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [modalAccount, setModalAccount] = useState(null);
  const [loadingAccountModal, setLoadingAccountModal] = useState(false);
  const [showBookAppointmentModal, setShowBookAppointmentModal] =
    useState(false);
  const [caseAppointments, setCaseAppointments] = useState([]);
  const [showSendEmail, setShowSendEmail] = useState(false);

  // 3-dot menu and delete confirmation
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Reminder state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderNote, setReminderNote] = useState("");
  const [reminderSaving, setReminderSaving] = useState(false);

  // Contact timer state
  const [contactTimerNow, setContactTimerNow] = useState(Date.now());
  const [contactSaving, setContactSaving] = useState(false);

  // Related data
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [files, setFiles] = useState([]);

  // Form states
  const [formData, setFormData] = useState({});
  const [newNote, setNewNote] = useState("");
  const [noteImages, setNoteImages] = useState([]); // { file, previewUrl }[]
  const [noteImageUploading, setNoteImageUploading] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedNewOwner, setSelectedNewOwner] = useState("");

  // Drag and drop states
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);

  // Initial data fetch
  useEffect(() => {
    fetchCaseData();
    fetchNotes();
    fetchAttachments();
    fetchActivities();
    fetchUsers();
  }, [id]);

  // Live-tick the contact timer every second while the case is not yet contacted
  useEffect(() => {
    const tick = setInterval(() => setContactTimerNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  // Fetch files when tab changes to files
  useEffect(() => {
    if (activeTab === "files") {
      fetchFiles();
    }
  }, [activeTab, id]);

  // Fetch appointments when tab changes to appointments
  useEffect(() => {
    if (activeTab === "appointments") {
      fetchCaseAppointments();
    }
  }, [activeTab, id]);

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";
  };

  const fetchCaseData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCase(id);
      setCaseData(response.data);
      setFormData(response.data);
      setReminderNote(response.data.reminderNote || "");
    } catch (error) {
      console.error("Error fetching case:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getCaseNotes(id);
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getCaseAttachments(id);
      setAttachments(response.data || []);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await apiService.getCaseActivity(id);
      setActivities(response.data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await apiService.getCaseFiles(id);
      setFiles(response.data || []);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setAllUsers(response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchCaseAppointments = async () => {
    try {
      const response = await apiService.getCaseAppointments(id);
      setCaseAppointments(response.data || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };

  const handleSave = async () => {
    try {
      await apiService.updateCase(id, formData);
      setCaseData(formData);
      setEditMode(false);
      fetchActivities();
    } catch (error) {
      console.error("Error updating case:", error);
      alert("Failed to update case");
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteCase(id);
      navigate("/cases");
    } catch (error) {
      console.error("Error deleting case:", error);
      alert("Failed to delete case");
    }
  };

  const handleSetReminder = async () => {
    try {
      setReminderSaving(true);
      const response = await apiService.setCaseReminder(id, true, reminderNote);
      setCaseData((prev) => ({
        ...prev,
        reminderEnabled: true,
        reminderNote: reminderNote || null,
        reminderSetAt: response.data?.reminderSetAt,
      }));
      setShowReminderModal(false);
    } catch (error) {
      console.error("Error setting reminder:", error);
      alert("Failed to set reminder");
    } finally {
      setReminderSaving(false);
    }
  };

  const handleRemoveReminder = async () => {
    try {
      setReminderSaving(true);
      await apiService.setCaseReminder(id, false);
      setCaseData((prev) => ({
        ...prev,
        reminderEnabled: false,
        reminderNote: null,
        reminderSetAt: null,
      }));
      setReminderNote("");
      setShowReminderModal(false);
    } catch (error) {
      console.error("Error removing reminder:", error);
      alert("Failed to remove reminder");
    } finally {
      setReminderSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setNoteImageUploading(noteImages.length > 0);

      // Upload each selected image first, collect their s3Keys
      const uploadedImages = await Promise.all(
        noteImages.map(async ({ file }) => {
          const formData = new FormData();
          formData.append("image", file);
          // Temporary note id doesn't exist yet — we upload after note creation instead
          // So we pass images as metadata after note is created
          return file; // placeholder; handled below
        }),
      );

      // Create the note first (without images)
      const noteRes = await apiService.createCaseNote(id, { content: newNote });
      const noteId = noteRes.data.id;

      // Upload images to the new note and collect metadata
      const imageMetadata = await Promise.all(
        noteImages.map(async ({ file }) => {
          const fd = new FormData();
          fd.append("image", file);
          const res = await apiService.uploadNoteImage(id, noteId, fd);
          return res.data;
        }),
      );

      setNoteImages([]);
      setNewNote("");
      setShowNoteForm(false);
      setNoteImageUploading(false);
      fetchNotes();
      fetchActivities();
    } catch (error) {
      console.error("Error adding note:", error);
      setNoteImageUploading(false);
      alert("Failed to add note");
    }
  };

  const handleNoteImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newEntries = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNoteImages((prev) => [...prev, ...newEntries]);
    e.target.value = "";
  };

  const handleRemoveNoteImage = (index) => {
    setNoteImages((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].previewUrl);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      try {
        await apiService.deleteCaseNote(id, noteId);
        fetchNotes();
        fetchActivities();
      } catch (error) {
        console.error("Error deleting note:", error);
        alert("Failed to delete note");
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadCaseFile(id, formData);
      fetchFiles();
      event.target.value = ""; // Reset input
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (window.confirm("Are you sure you want to delete this attachment?")) {
      try {
        await apiService.deleteCaseAttachment(id, attachmentId);
        fetchAttachments();
        fetchActivities();
      } catch (error) {
        console.error("Error deleting attachment:", error);
        alert("Failed to delete attachment");
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await apiService.deleteCaseFile(id, fileId);
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file");
    }
  };

  const handleChangeOwner = async () => {
    if (!selectedNewOwner) {
      alert("Please select a new owner");
      return;
    }

    try {
      await apiService.assignCase(id, selectedNewOwner);
      await fetchCaseData();
      setShowChangeOwner(false);
      setSelectedNewOwner("");
      alert("Case owner updated successfully");
    } catch (error) {
      console.error("Error changing owner:", error);
      alert("Failed to change case owner");
    }
  };

  // ─── Contact Timer helpers ────────────────────────────────────────────────
  const CONTACT_DEADLINE_MS = 4 * 60 * 60 * 1000; // 4 hours

  const getContactTimerInfo = () => {
    if (!caseData) return null;
    if (caseData.contactedAt) return { done: true };
    const start = new Date(
      caseData.contactTimerStartedAt || caseData.createdAt,
    );
    const elapsed = contactTimerNow - start.getTime();
    const remaining = CONTACT_DEADLINE_MS - elapsed;
    return { done: false, remaining, overdue: remaining <= 0 };
  };

  const formatCountdown = (ms) => {
    if (ms <= 0) return "Overdue";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  };

  const formatElapsed = (seconds) => {
    if (!seconds && seconds !== 0) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const handleMarkContacted = async (contacted) => {
    try {
      setContactSaving(true);
      const response = await apiService.markCaseContacted(id, contacted);
      setCaseData((prev) => ({
        ...prev,
        contactedAt: response.data?.contactedAt || null,
        contactedById: response.data?.contactedById || null,
        contactTimeElapsedSeconds:
          response.data?.contactTimeElapsedSeconds ?? null,
      }));
    } catch (error) {
      console.error("Error updating contacted status:", error);
      alert("Failed to update contact status");
    } finally {
      setContactSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFilesToUpload(files);
      setShowUploadConfirmation(true);
    }
  };

  const confirmUpload = async () => {
    if (!filesToUpload) return;

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        await apiService.uploadCaseFile(id, formData);
      }
      fetchFiles();
      setShowUploadConfirmation(false);
      setFilesToUpload(null);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files");
    }
  };

  const cancelUpload = () => {
    setShowUploadConfirmation(false);
    setFilesToUpload(null);
  };

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      New: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      "In Progress": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      "On Hold": "bg-orange-500/20 text-orange-400 border-orange-500/30",
      Escalated: "bg-red-500/20 text-red-400 border-red-500/30",
      Closed: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    };
    return colors[status] || colors.New;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      Medium: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      High: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    return colors[priority] || colors.Medium;
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith("image/")) return "🖼️";
    if (fileType?.startsWith("video/")) return "🎥";
    if (fileType?.startsWith("audio/")) return "🎵";
    if (fileType?.includes("pdf")) return "📄";
    if (fileType?.includes("word") || fileType?.includes("document"))
      return "📝";
    if (fileType?.includes("sheet") || fileType?.includes("excel")) return "📊";
    if (fileType?.includes("presentation") || fileType?.includes("powerpoint"))
      return "📽️";
    if (fileType?.includes("zip") || fileType?.includes("rar")) return "🗜️";
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFullFileUrl = (fileUrl) => {
    if (!fileUrl) return null;
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      return fileUrl;
    }
    const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";
    return S3_BASE_URL + fileUrl;
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Case not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Go Back</span>
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1
                    className={`text-xl sm:text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Case #{caseData.caseNumber}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                      caseData.status,
                    )}`}
                  >
                    {caseData.status}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                      caseData.priority,
                    )}`}
                  >
                    {caseData.priority}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormData(caseData);
                    }}
                    className={`px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                  >
                    <span className="hidden sm:inline">Cancel</span>
                    <X className="w-4 h-4 sm:hidden" />
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">Save Changes</span>
                  </button>
                </>
              ) : (
                <>
                  {/* ─── Contact Timer Button ─── */}
                  {(() => {
                    const info = getContactTimerInfo();
                    if (!info) return null;
                    if (info.done) {
                      return (
                        <button
                          onClick={() => handleMarkContacted(false)}
                          disabled={contactSaving}
                          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg transition-colors text-sm"
                          title={`Contacted — elapsed: ${formatElapsed(caseData.contactTimeElapsedSeconds)}`}
                        >
                          <PhoneCall className="w-4 h-4" />
                          <span className="hidden sm:inline">Contacted ✓</span>
                          <span className="text-green-300 text-xs ml-1 hidden sm:inline">
                            ({formatElapsed(caseData.contactTimeElapsedSeconds)}
                            )
                          </span>
                        </button>
                      );
                    }
                    const urgent = info.remaining <= 60 * 60 * 1000; // ≤ 1 hr
                    return (
                      <button
                        onClick={() => handleMarkContacted(true)}
                        disabled={contactSaving}
                        className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                          info.overdue
                            ? "bg-red-700 hover:bg-red-800 text-white animate-pulse"
                            : urgent
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : "bg-orange-600 hover:bg-orange-700 text-white"
                        }`}
                        title="Click to mark case as contacted"
                      >
                        <Clock className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {info.overdue ? "OVERDUE — " : ""}Mark Contacted
                        </span>
                        <span
                          className={`text-xs ml-1 hidden sm:inline ${info.overdue ? "text-red-200" : urgent ? "text-red-200" : "text-orange-200"}`}
                        >
                          ({formatCountdown(info.remaining)})
                        </span>
                      </button>
                    );
                  })()}
                  {/* ────────────────────────────── */}
                  <button
                    onClick={() => setEditMode(true)}
                    className={`px-3 sm:px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                  >
                    <Edit className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button
                    onClick={() => setShowSendEmail(true)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Mail size={16} />
                    <span className="hidden sm:inline">Send Email</span>
                  </button>
                  {/* 3-dot More Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setShowMoreMenu((v) => !v)}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                      title="More options"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {showMoreMenu && (
                      <>
                        {/* Backdrop to close on outside click */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowMoreMenu(false)}
                        />
                        <div
                          className={`absolute right-0 top-full mt-2 w-52 rounded-lg shadow-xl z-20 py-1 overflow-hidden ${isDarkMode ? "bg-[#1C2028] border border-gray-700" : "bg-white border border-gray-200"}`}
                        >
                          <button
                            onClick={() => {
                              setShowMoreMenu(false);
                              setReminderNote(caseData.reminderNote || "");
                              setShowReminderModal(true);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDarkMode ? "text-gray-200 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100"}`}
                          >
                            {caseData.reminderEnabled ? (
                              <Bell className="w-4 h-4 text-amber-400" />
                            ) : (
                              <BellOff className="w-4 h-4 text-gray-400" />
                            )}
                            <span>
                              {caseData.reminderEnabled
                                ? "Reminder On — Edit"
                                : "Set Reminder"}
                            </span>
                            {caseData.reminderEnabled && (
                              <span className="ml-auto w-2 h-2 rounded-full bg-amber-400" />
                            )}
                          </button>
                          <div
                            className={`border-t my-1 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                          />
                          <button
                            onClick={() => {
                              setShowMoreMenu(false);
                              setShowDeleteModal(true);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Case
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div
            className={`flex mt-4 border-b overflow-x-auto scrollbar-hide ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            {["details", "feed", "appointments", "emails", "files"].map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 px-4 sm:px-6 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-400"
                      : isDarkMode
                        ? "border-transparent text-gray-400 hover:text-white"
                        : "border-transparent text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === "files" && files.length > 0 && ` (${files.length})`}
                </button>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        {activeTab === "details" && (
          <div className="space-y-6">
            {/* Account Information */}
            {caseData.account && (
              <div
                className={`border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <button
                  onClick={() => toggleSection("accountInfo")}
                  className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                >
                  <h2
                    className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Building2
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    Account Information
                  </h2>
                  {expandedSections.accountInfo ? (
                    <ChevronDown
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  ) : (
                    <ChevronRight
                      className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  )}
                </button>

                {expandedSections.accountInfo && (
                  <div className="px-6 pb-6">
                    <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
                      <div className="w-16 h-16 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                        {getInitials(caseData.account.name)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <button
                            onClick={() => setShowAccountModal(true)}
                            className={`text-xl font-bold transition-colors ${isDarkMode ? "text-white hover:text-blue-400" : "text-gray-900 hover:text-blue-600"}`}
                          >
                            {caseData.account.name}
                          </button>
                          {caseData.account.type && (
                            <span className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                              {caseData.account.type}
                            </span>
                          )}
                        </div>

                        {/* Account Contact Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                          {caseData.account.phone && (
                            <div className="p-2">
                              <PhoneLink
                                phoneNumber={caseData.account.phone}
                                entityType="account"
                                entityId={caseData.account.id}
                                entityName={caseData.account.name}
                              />
                            </div>
                          )}
                          {caseData.account.email && (
                            <a
                              href={`mailto:${caseData.account.email}`}
                              className={`flex items-center gap-2 text-sm transition-colors p-2 rounded-lg ${isDarkMode ? "text-gray-300 hover:text-blue-400 hover:bg-gray-800/50" : "text-gray-600 hover:text-blue-600 hover:bg-gray-100"}`}
                            >
                              <Mail className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">
                                {caseData.account.email}
                              </span>
                            </a>
                          )}
                          {caseData.account.website && (
                            <a
                              href={caseData.account.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 text-sm transition-colors p-2 rounded-lg ${isDarkMode ? "text-gray-300 hover:text-blue-400 hover:bg-gray-800/50" : "text-gray-600 hover:text-blue-600 hover:bg-gray-100"}`}
                            >
                              <Globe className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">
                                {caseData.account.website}
                              </span>
                            </a>
                          )}
                          {(caseData.account.billingCity ||
                            caseData.account.billingState) && (
                            <div
                              className={`flex items-center gap-2 text-sm p-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">
                                {[
                                  caseData.account.billingCity,
                                  caseData.account.billingState,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Primary Contact Information */}
                        {caseData.contact && (
                          <div
                            className={`mb-4 p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                          >
                            <div
                              className={`text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Primary Contact
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-purple-400" />
                              <span
                                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {caseData.contact.firstName}{" "}
                                {caseData.contact.lastName}
                              </span>
                              {caseData.contact.title && (
                                <span
                                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  • {caseData.contact.title}
                                </span>
                              )}
                            </div>

                            <div className="space-y-1.5 mt-2">
                              {caseData.contact.email && (
                                <a
                                  href={`mailto:${caseData.contact.email}`}
                                  className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                >
                                  <Mail className="w-4 h-4" />
                                  {caseData.contact.email}
                                </a>
                              )}
                              {caseData.contact.phone && (
                                <PhoneLink
                                  phoneNumber={caseData.contact.phone}
                                  entityType="contact"
                                  entityId={caseData.contact.id}
                                  entityName={`${caseData.contact.firstName} ${caseData.contact.lastName}`}
                                />
                              )}
                              {caseData.contact.mobilePhone && (
                                <div className="flex items-center gap-2">
                                  <PhoneLink
                                    phoneNumber={caseData.contact.mobilePhone}
                                    entityType="contact"
                                    entityId={caseData.contact.id}
                                    entityName={`${caseData.contact.firstName} ${caseData.contact.lastName}`}
                                  />
                                  <span
                                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                  >
                                    (Mobile)
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setShowAccountModal(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View Details
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/accounts/${caseData.account.id}`)
                            }
                            className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                          >
                            Go to Account Page
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Case Information */}
            <div
              className={`border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => toggleSection("caseInfo")}
                className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
              >
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Case Information
                </h2>
                {expandedSections.caseInfo ? (
                  <ChevronDown
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                ) : (
                  <ChevronRight
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                )}
              </button>

              {expandedSections.caseInfo && (
                <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Account */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Account
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.account?.name || ""}
                        disabled
                        className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-50 border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Building2
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {caseData.account?.name || "—"}
                      </div>
                    )}
                  </div>

                  {/* Contact */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Contact
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={
                          formData.contact
                            ? `${formData.contact.firstName} ${formData.contact.lastName}`
                            : ""
                        }
                        disabled
                        className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-50 border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <User
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {caseData.contact
                          ? `${caseData.contact.firstName} ${caseData.contact.lastName}`
                          : "—"}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Status
                    </label>
                    {editMode ? (
                      <select
                        value={formData.status}
                        onChange={(e) =>
                          setFormData({ ...formData, status: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      >
                        <option value="New">New</option>
                        <option value="In Progress">In Progress</option>
                        <option value="On Hold">On Hold</option>
                        <option value="Escalated">Escalated</option>
                        <option value="Closed">Closed</option>
                      </select>
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.status}
                      </div>
                    )}
                  </div>

                  {/* Priority */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Priority
                    </label>
                    {editMode ? (
                      <select
                        value={formData.priority}
                        onChange={(e) =>
                          setFormData({ ...formData, priority: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </select>
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.priority}
                      </div>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Type
                    </label>
                    {editMode ? (
                      <select
                        value={formData.type || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, type: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      >
                        <option value="">--None--</option>
                        <option value="Technical Fault Line 1">
                          Technical Fault 1
                        </option>
                        <option value="Technical Fault Line 2">
                          Technical Fault 2
                        </option>
                        <option value="ESCALATIONS">ESCALATIONS</option>
                        <option value="migrations">Migrations</option>
                        <option value="Base Fault Queue">
                          Base Fault Queue
                        </option>
                        <option value="Install Problems">
                          Install Problems
                        </option>
                        <option value="Porting">Porting</option>
                        <option value="Revisit Required">
                          Revisit Required
                        </option>
                        <option value="Remote Install Queue">
                          Remote Install Queue
                        </option>
                        <option value="Temp Cease of Services">
                          Temp Cease of Services
                        </option>
                        <option value="Permanent Case">Permanent Case</option>
                        <option value="Office Issue">Office Issue</option>
                        <option value="Billing Query">Billing Query</option>
                        <option value="Sales Issue">Sales Issue</option>
                        <option value="Printer Issue">Printer Issue</option>
                        <option value="Additional Equipment">
                          Additional Equipment
                        </option>
                        <option value="CCTV Fault">CCTV Fault</option>
                      </select>
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.type
                          ? caseData.type.charAt(0).toUpperCase() +
                            caseData.type.slice(1)
                          : "—"}
                      </div>
                    )}
                  </div>

                  {/* Case Origin */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Case Origin
                    </label>
                    {editMode ? (
                      <select
                        value={formData.origin || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, origin: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      >
                        <option value="">--None--</option>
                        <option value="Email">Email</option>
                        <option value="Phone">Phone</option>
                        <option value="Web">Web</option>
                      </select>
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.origin || "—"}
                      </div>
                    )}
                  </div>

                  {/* Case Reason */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Case Reason
                    </label>
                    {editMode ? (
                      <select
                        value={formData.reason || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, reason: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      >
                        <option value="">--None--</option>
                        <option value="User didn't attend training">
                          User didn't attend training
                        </option>
                        <option value="Complex functionality">
                          Complex functionality
                        </option>
                        <option value="Existing problem">
                          Existing problem
                        </option>
                        <option value="Instructions not clear">
                          Instructions not clear
                        </option>
                        <option value="New problem">New problem</option>
                      </select>
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.reason || "—"}
                      </div>
                    )}
                  </div>

                  {/* Owner */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Case Owner
                    </label>
                    {editMode ? (
                      <div className="space-y-2">
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          <User
                            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          {caseData.owner?.name || "—"}
                        </div>
                        <button
                          onClick={() => setShowChangeOwner(true)}
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Change Owner
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div
                          className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          <User
                            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          {caseData.owner?.name || "—"}
                        </div>
                        <button
                          onClick={() => setShowChangeOwner(true)}
                          className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Created Date */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Created Date
                    </label>
                    <div
                      className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <Calendar
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      />
                      {formatDate(caseData.createdAt)}
                    </div>
                  </div>

                  {/* Last Modified */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Last Modified
                    </label>
                    <div
                      className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      <Clock
                        className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      />
                      {formatDate(caseData.updatedAt)}
                    </div>
                  </div>

                  {/* Closed Date */}
                  {caseData.closedDate && (
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Closed Date
                      </label>
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {formatDate(caseData.closedDate)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Description Information */}
            <div
              className={`border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => toggleSection("description")}
                className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
              >
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Description Information
                </h2>
                {expandedSections.description ? (
                  <ChevronDown
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                ) : (
                  <ChevronRight
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                )}
              </button>

              {expandedSections.description && (
                <div className="px-6 pb-6 space-y-4">
                  {/* Subject */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Subject
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.subject || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, subject: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.subject || "—"}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Description
                    </label>
                    {editMode ? (
                      <textarea
                        rows="4"
                        value={formData.description || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {caseData.description || "—"}
                      </div>
                    )}
                  </div>

                  {/* Internal Comments */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Internal Comments
                    </label>
                    {editMode ? (
                      <textarea
                        rows="3"
                        value={formData.internalComments || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            internalComments: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`whitespace-pre-wrap ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {caseData.internalComments || "—"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div
              className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Notes
                </h2>
                <button
                  onClick={() => setShowNoteForm(!showNoteForm)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Note
                </button>
              </div>

              {showNoteForm && (
                <div
                  className={`mb-6 p-4 border rounded-lg ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <textarea
                    rows="4"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note..."
                    className={`w-full px-3 py-2 border rounded-lg placeholder-gray-500 focus:ring-2 focus:ring-blue-500 resize-none mb-3 ${isDarkMode ? "bg-[#13161B] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  />

                  {/* Image previews */}
                  {noteImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {noteImages.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.previewUrl}
                            alt="preview"
                            className={`w-24 h-24 object-cover rounded-lg border ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                          />
                          <button
                            onClick={() => handleRemoveNoteImage(idx)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 items-center">
                    <button
                      onClick={handleAddNote}
                      disabled={noteImageUploading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {noteImageUploading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" /> Saving...
                        </>
                      ) : (
                        "Save Note"
                      )}
                    </button>
                    {/* Image picker */}
                    <label
                      className={`px-3 py-2 rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                    >
                      <Image className="w-4 h-4" />
                      <span className="text-sm">Add Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleNoteImageSelect}
                      />
                    </label>
                    <button
                      onClick={() => {
                        setShowNoteForm(false);
                        setNewNote("");
                        noteImages.forEach((img) =>
                          URL.revokeObjectURL(img.previewUrl),
                        );
                        setNoteImages([]);
                      }}
                      className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {notes.length === 0 ? (
                <div
                  className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  <FileText
                    className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                  />
                  <p>No notes yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className={`p-4 border rounded-lg ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm text-white">
                            {note.creator?.name?.charAt(0) || "?"}
                          </div>
                          <div>
                            <div
                              className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {note.creator?.name || "Unknown"}
                            </div>
                            <div
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {formatDate(note.createdAt)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p
                        className={`whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {note.body}
                      </p>
                      {/* Inline images */}
                      {note.customFields?.images?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {note.customFields.images.map((img, idx) => (
                            <a
                              key={idx}
                              href={img.signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={img.signedUrl}
                                alt={img.fileName || `Image ${idx + 1}`}
                                className={`max-h-48 max-w-xs rounded-lg border object-cover hover:opacity-90 transition-opacity cursor-pointer ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Web Information */}
            <div
              className={`border rounded-lg overflow-hidden ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <button
                onClick={() => toggleSection("webInfo")}
                className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
              >
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Web Information
                </h2>
                {expandedSections.webInfo ? (
                  <ChevronDown
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                ) : (
                  <ChevronRight
                    className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                )}
              </button>

              {expandedSections.webInfo && (
                <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Web Email */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Web Email
                    </label>
                    {editMode ? (
                      <input
                        type="email"
                        value={formData.webEmail || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, webEmail: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Mail
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {caseData.webEmail || "—"}
                      </div>
                    )}
                  </div>

                  {/* Web Name */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Web Name
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.webName || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, webName: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {caseData.webName || "—"}
                      </div>
                    )}
                  </div>

                  {/* Web Company */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Web Company
                    </label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.webCompany || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            webCompany: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Building2
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {caseData.webCompany || "—"}
                      </div>
                    )}
                  </div>

                  {/* Web Phone */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Web Phone
                    </label>
                    {editMode ? (
                      <input
                        type="tel"
                        value={formData.webPhone || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, webPhone: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Phone
                          className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        {caseData.webPhone || "—"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "feed" && (
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Activity Feed
                </h2>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Updates and comments from Salesforce Chatter
                </p>
              </div>
              <button
                onClick={() =>
                  apiService.syncSalesforceFeed({
                    parentTypes: ["Case"],
                    limit: 1000,
                  })
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Sync Feed
              </button>
            </div>
            <CaseFeedItems caseId={id} />
          </div>
        )}

        {activeTab === "emails" && (
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Emails
              </h2>
              <button
                onClick={() => setShowSendEmail(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Send Email
              </button>
            </div>
            <CaseEmailThread caseId={id} caseData={caseData} />
          </div>
        )}

        {activeTab === "appointments" && (
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Appointments
              </h2>
              <button
                onClick={() => setShowBookAppointmentModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Book Appointment</span>
              </button>
            </div>

            {caseAppointments.length === 0 ? (
              <div
                className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                <Calendar
                  className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                />
                <p>No appointments scheduled</p>
              </div>
            ) : (
              <div className="space-y-4">
                {caseAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className={`p-4 border rounded-lg hover:border-blue-500/50 transition-colors cursor-pointer ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                    onClick={() => navigate(`/appointments/${appointment.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3
                          className={`font-medium mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {appointment.title}
                        </h3>
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {appointment.description}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          appointment.status === "completed"
                            ? "bg-green-500/20 text-green-400"
                            : appointment.status === "cancelled"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-blue-500/20 text-blue-400"
                        }`}
                      >
                        {appointment.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Calendar className="w-4 h-4" />
                        {formatDate(appointment.startTime)}
                      </div>
                      <div
                        className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <User className="w-4 h-4" />
                        {appointment.assignee?.name}
                      </div>
                      {appointment.location && (
                        <div
                          className={`flex items-center gap-2 col-span-1 sm:col-span-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <MapPin className="w-4 h-4" />
                          {appointment.location}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "files" && (
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Files ({files.length})
              </h2>
              <label className="cursor-pointer">
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Upload File</span>
                </div>
              </label>
            </div>

            {files.length === 0 ? (
              <div
                className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                <Paperclip
                  className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                />
                <p className="mb-4">No files attached</p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="inline-flex items-center text-white gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                    <Plus className="w-4 h-4" />
                    Upload First File
                  </div>
                </label>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className={`group flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                  >
                    <div className="text-2xl">{getFileIcon(file.fileType)}</div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        title={file.title}
                      >
                        {file.title}
                      </div>
                      <div
                        className={`text-xs flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <span>{formatFileSize(file.contentSize)}</span>
                        <span>•</span>
                        <span>
                          {new Date(file.createdAt).toLocaleDateString()}
                        </span>
                        {file.creator && (
                          <>
                            <span>•</span>
                            <span>{file.creator.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {file.fileUrl && (
                        <a
                          href={getFullFileUrl(file.fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                          title="Download"
                        >
                          <ExternalLink size={16} className="text-blue-400" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                        title="Delete"
                      >
                        <Trash2 size={16} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAccountModal && caseData.account && (
        <AccountDetailModal
          accountId={caseData.account.id}
          onClose={() => setShowAccountModal(false)}
          onNavigate={(accountId) => {
            setShowAccountModal(false);
            navigate(`/accounts/${accountId}`);
          }}
        />
      )}

      {showBookAppointmentModal && (
        <BookCaseAppointmentModal
          caseData={caseData}
          onClose={() => setShowBookAppointmentModal(false)}
          onSuccess={(appointment) => {
            setShowBookAppointmentModal(false);
            fetchCaseAppointments();
            fetchActivities();
          }}
        />
      )}

      {showSendEmail && (
        <SendCaseEmailModal
          caseData={caseData}
          onClose={() => setShowSendEmail(false)}
          onSuccess={() => {
            setShowSendEmail(false);
            fetchCaseData();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`border rounded-xl max-w-md w-full shadow-2xl ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3
                    className={`text-lg font-semibold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Delete Case #{caseData.caseNumber}?
                  </h3>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    This will permanently delete this case and all its
                    associated data, including notes, attachments, and activity
                    history. This action{" "}
                    <span className="text-red-400 font-medium">
                      cannot be undone
                    </span>
                    .
                  </p>
                </div>
              </div>
            </div>
            <div
              className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <button
                onClick={() => setShowDeleteModal(false)}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDelete();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Yes, Delete Case
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`border rounded-lg max-w-md w-full ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                <h3
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {caseData.reminderEnabled
                    ? "Edit Reminder"
                    : "Set Daily Reminder"}
                </h3>
              </div>
              <button
                onClick={() => setShowReminderModal(false)}
                className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                A daily email reminder will be sent at 9am for case{" "}
                <span
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  #{caseData.caseNumber}
                </span>{" "}
                until it is closed or the reminder is removed.
              </p>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Reminder Note{" "}
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    {" "}
                    (optional)
                  </span>
                </label>
                <textarea
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  placeholder="e.g. Waiting on customer callback, follow up daily"
                  rows={3}
                  className={`w-full border rounded-lg px-3 py-2 placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm resize-none ${isDarkMode ? "bg-[#0C0E12] border-gray-600 text-white" : "bg-gray-50 border-gray-300 text-gray-900"}`}
                />
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  This note will appear in the reminder email.
                </p>
              </div>
            </div>
            <div
              className={`px-6 py-4 border-t flex items-center justify-between gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <div>
                {caseData.reminderEnabled && (
                  <button
                    onClick={handleRemoveReminder}
                    disabled={reminderSaving}
                    className="px-4 py-2 text-red-400 hover:text-red-300 text-sm transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    <BellOff className="w-4 h-4" />
                    Remove Reminder
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowReminderModal(false)}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetReminder}
                  disabled={reminderSaving}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {reminderSaving ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  {caseData.reminderEnabled
                    ? "Update Reminder"
                    : "Enable Reminder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Owner Modal */}
      {showChangeOwner && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`border rounded-lg max-w-md w-full ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div
              className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <h3
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Change Case Owner
              </h3>
              <button
                onClick={() => {
                  setShowChangeOwner(false);
                  setSelectedNewOwner("");
                }}
                className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Current Owner
                </label>
                <div
                  className={`flex items-center gap-2 p-3 border rounded-lg ${isDarkMode ? "text-white bg-[#0C0E12] border-gray-700" : "text-gray-900 bg-gray-50 border-gray-200"}`}
                >
                  <User
                    className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                  {caseData.owner?.name || "—"}
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  New Owner *
                </label>
                <select
                  value={selectedNewOwner}
                  onChange={(e) => setSelectedNewOwner(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="">Select a user...</option>
                  {allUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.email ? `(${user.email})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <button
                onClick={() => {
                  setShowChangeOwner(false);
                  setSelectedNewOwner("");
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-200 text-gray-900 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeOwner}
                disabled={!selectedNewOwner}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change Owner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
          <div
            className={`rounded-lg border-2 border-dashed border-blue-500 p-8 text-center ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
          >
            <Paperclip size={48} className="mx-auto mb-4 text-blue-400" />
            <p
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Drop files to upload
            </p>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Release to add files to this case
            </p>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal */}
      {showUploadConfirmation && filesToUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Paperclip className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Upload Files?
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {filesToUpload.length === 1
                    ? `Upload "${filesToUpload[0].name}" to this case?`
                    : `Upload ${filesToUpload.length} files to this case?`}
                </p>

                {filesToUpload.length > 1 && (
                  <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                    {Array.from(filesToUpload).map((file, idx) => (
                      <div
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Paperclip size={14} />
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelUpload}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDetail;
